"use client"

/**
 * `ShipFromAddressPicker` — single-select picker for the per-listing
 * ship-from override per ADR_LISTING_SHIP_FROM_ADDRESS.md (Phase 1).
 *
 * UX (ADR §"UX (listing creation form)"):
 *  - Default state: shows the seller's main address ("Ships from my main
 *    address") as a static preview. No picker affordance when there's
 *    only one saved address.
 *  - With 2+ saved addresses: a subtle "Use a different address"
 *    disclosure reveals a DropdownMenu listing every saved address.
 *  - Picking the main row clears the pin (`onChange(null)` — column stays
 *    NULL, resolution falls back to `is_main`). Picking a non-main row
 *    writes `address_book.id` to `ship_from_address_id`.
 *  - No "Add new address" affordance in Phase 1; we link out to settings.
 *
 * Three-layer rule: this is the Container layer (Behavior + data).
 * Data layer is the existing `GET /api/address-book` route — no direct
 * `supabase.from(...)` (HARD GATE Silent NACK). Style mirrors the
 * Address Book dropdown in `components/cart/shipping-form.tsx:574-656`.
 *
 * @see .tondocs/listings/ADR_LISTING_SHIP_FROM_ADDRESS.md
 * @see .tondocs/skills/react-primitive-design.md §"three-layer architecture"
 * @see components/cart/shipping-form.tsx — dropdown pattern reference
 */

import React, { useEffect, useState, useMemo } from "react"
import Link from "next/link"
import { ChevronDown, CheckCircle2, Loader2, Settings } from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Label } from "@/components/ui/label"
import * as PayPalCountries from "@/lib/paypal-countries"
import { cn } from "@/lib/utils"

// ---------------------------------------------------------------------------
// API row — mirrors GET /api/address-book response shape
// ---------------------------------------------------------------------------

export interface SavedAddress {
  id: string
  label?: string | null
  first_name?: string | null
  last_name?: string | null
  address: string
  apartment?: string | null
  city: string
  state?: string | null
  postal: string
  country: string
  phone?: string | null
  email?: string | null
  is_main: boolean
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatAddressPreview(address: SavedAddress): string {
  const stateValue = address.state
    ? PayPalCountries.getStateName(address.state, address.country)
    : ""
  const segments = [
    address.address,
    address.city,
    stateValue,
    address.postal,
    PayPalCountries.getCountryName(address.country),
  ].filter((segment): segment is string => Boolean(segment && segment.trim().length > 0))
  return segments.join(", ")
}

function addressLabel(address: SavedAddress): string {
  if (address.label && address.label.trim()) return address.label
  if (address.is_main) return "Main address"
  const first = address.first_name?.trim() ?? ""
  const last = address.last_name?.trim() ?? ""
  const name = `${first} ${last}`.trim()
  return name || `${address.city}, ${address.country}`
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface ShipFromAddressPickerProps {
  /**
   * Current pin. `null` ≡ "use my main address" — column stays NULL,
   * resolution falls back to `is_main` at rate-fetch time.
   */
  value: string | null
  /**
   * Fires when the seller picks a different row. `null` means "go back
   * to using my main address" (writes `null` to `ship_from_address_id`).
   */
  onChange: (nextAddressId: string | null) => void
  compact?: boolean
  className?: string
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ShipFromAddressPicker({
  value,
  onChange,
  compact = false,
  className,
}: ShipFromAddressPickerProps): React.ReactElement {
  const [addresses, setAddresses] = useState<SavedAddress[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    void (async () => {
      try {
        const res = await fetch("/api/address-book")
        if (!res.ok) {
          if (!cancelled) {
            setError("Could not load saved addresses")
            setAddresses([])
          }
          return
        }
        const body = (await res.json()) as unknown
        if (cancelled) return
        if (Array.isArray(body)) {
          setAddresses(body as SavedAddress[])
        } else {
          setAddresses([])
        }
      } catch {
        if (!cancelled) {
          setError("Could not load saved addresses")
          setAddresses([])
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const mainAddress = useMemo(
    () => addresses.find((a) => a.is_main) ?? null,
    [addresses],
  )

  const selectedAddress = useMemo(() => {
    if (value) return addresses.find((a) => a.id === value) ?? null
    return mainAddress
  }, [addresses, mainAddress, value])

  const hasMultipleAddresses = addresses.length > 1
  const sectionLabelClass =
    "text-xs font-semibold uppercase tracking-wide text-foreground/80"
  const cardClass =
    "rounded-3xl border border-sheet-handle bg-sheet text-foreground"

  // No addresses on file — gentle nudge into settings.
  if (!loading && addresses.length === 0) {
    return (
      <div className={cn("space-y-3", className)}>
        <Label className={cn(sectionLabelClass, "px-0.5")}>Ships from</Label>
        <div className={cn(cardClass, "px-4 py-3 text-sm")}>
          <p className="font-semibold">No saved address yet</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Add a main address in{" "}
            <Link
              href="/settings/address-book"
              className="underline underline-offset-2"
            >
              Settings → Address Book
            </Link>{" "}
            so buyers see live rates from your origin.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className={cn("space-y-3", className)}>
      <Label className={cn(sectionLabelClass, "px-0.5")}>Ships from</Label>

      {loading ? (
        <div
          className={cn(
            cardClass,
            "flex items-center gap-2 px-4 py-3 text-sm text-muted-foreground",
          )}
        >
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading addresses…
        </div>
      ) : !hasMultipleAddresses ? (
        // Single address (just the main) — no picker affordance, static preview.
        <div className={cn(cardClass, "px-4 py-3 space-y-1")}>
          <p className="text-sm font-semibold leading-tight line-clamp-1">
            Ships from my main address
          </p>
          {selectedAddress ? (
            <p className="text-xs line-clamp-2 wrap-break-word text-muted-foreground">
              {formatAddressPreview(selectedAddress)}
            </p>
          ) : null}
        </div>
      ) : (
        <>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className={cn(
                  "flex w-full items-center justify-between overflow-hidden rounded-3xl border px-4 py-3 text-left transition focus:outline-hidden focus:ring-2",
                  "border-sheet-handle bg-sheet text-foreground focus:ring-muted-foreground/70",
                  compact && "py-2.5",
                )}
              >
                <div className="min-w-0 max-w-full flex-1 space-y-1">
                  <p className="text-sm font-semibold leading-tight line-clamp-1 wrap-break-word">
                    {selectedAddress ? (
                      <span className="flex min-w-0 items-center gap-2">
                        <span className="line-clamp-1 wrap-break-word">
                          {value === null
                            ? "Ships from my main address"
                            : addressLabel(selectedAddress)}
                        </span>
                        {selectedAddress.is_main && value === null && (
                          <span className="shrink-0 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-500">
                            Main
                          </span>
                        )}
                      </span>
                    ) : (
                      "Use a different address"
                    )}
                  </p>
                  <p className="text-xs line-clamp-2 wrap-break-word text-muted-foreground">
                    {selectedAddress
                      ? formatAddressPreview(selectedAddress)
                      : "Tap to pick a saved address"}
                  </p>
                </div>
                <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              // `chrome` surface lifts the portal to SHEET_Z_PICKER
              // (z-[10140]) — required when the picker is rendered inside
              // a sheet/dialog overlay (SHEET_Z_GLOBAL = 10130). The
              // default `panel` surface gets z-1500, which sits BEHIND
              // the sheet so the dropdown was visually hidden / clicks
              // never landed. Unconditional because BOTH consumers of this
              // picker (creation-flow `shipping-step`, edit-flow
              // `EditShippingTab`) render it inside an overlay — there is
              // no non-overlay callsite. Until 2026-08-01 it was keyed off
              // the dark-sheet prop, which was always `true` for exactly
              // that reason. See lib/dropdown-styles.ts §portal-z.
              surface="chrome"
              className={cn(
                "w-(--radix-dropdown-menu-trigger-width) space-y-1 max-h-[70vh] overflow-y-auto rounded-3xl",
              )}
            >
              {addresses.map((addr) => {
                // The "main" row sets pin to null (legacy fallback semantics);
                // all other rows write their id.
                const nextValue: string | null = addr.is_main ? null : addr.id
                const isSelected =
                  (value === null && addr.is_main) || value === addr.id
                return (
                  <DropdownMenuItem
                    key={addr.id}
                    onSelect={() => onChange(nextValue)}
                    className={cn(
                      "flex w-full items-center justify-between gap-3 rounded-2xl overflow-hidden",
                      "text-foreground hover:bg-muted focus:bg-muted focus:text-foreground",
                    )}
                  >
                    <div className="min-w-0 flex-1 space-y-1 text-left">
                      <div className="flex items-center gap-2">
                        <p className="min-w-0 flex-1 text-sm font-semibold leading-tight line-clamp-1 wrap-break-word">
                          {addressLabel(addr)}
                        </p>
                        {addr.is_main && (
                          <span className="shrink-0 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-500">
                            Main
                          </span>
                        )}
                      </div>
                      <p className="text-xs line-clamp-2 wrap-break-word text-muted-foreground">
                        {formatAddressPreview(addr)}
                      </p>
                    </div>
                    {isSelected && (
                      <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                    )}
                  </DropdownMenuItem>
                )
              })}
            </DropdownMenuContent>
          </DropdownMenu>

          <Link
            href="/settings/address-book"
            className="inline-flex items-center gap-1 text-xs underline-offset-2 hover:underline text-muted-foreground"
          >
            <Settings className="h-3 w-3" />
            Manage saved addresses
          </Link>
        </>
      )}

      {error ? (
        <p className="text-xs text-destructive">{error}</p>
      ) : null}
    </div>
  )
}
